import { createContext, useContext, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { WhatsAppFab } from "@/components/WhatsAppFab";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Solutions from "@/pages/Solutions";
import Method from "@/pages/Method";
import SolutionDetail from "@/pages/SolutionDetail";
import Blog from "@/pages/Blog";
import BlogArticle from "@/pages/BlogArticle";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import SeoLanding from "@/pages/SeoLanding";
import { SEO_PAGES } from "@/data/seoPages";

const LenisContext = createContext(null);
const useLenis = () => useContext(LenisContext);

const LenisProvider = ({ children }) => {
  const [lenis, setLenis] = useState(null);

  useEffect(() => {
    const instance = new Lenis({
      duration: 1.15,
      easing: (x) => Math.min(1, 1.001 - Math.pow(2, -10 * x)),
      smoothWheel: true,
    });
    let raf;
    const loop = (time) => {
      instance.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    setLenis(instance);
    return () => {
      cancelAnimationFrame(raf);
      instance.destroy();
    };
  }, []);

  return <LenisContext.Provider value={lenis}>{children}</LenisContext.Provider>;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const lenis = useLenis();
  useEffect(() => {
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, lenis]);
  return null;
};

function App() {
  return (
    <BrowserRouter>
      <LenisProvider>
        <ScrollToTop />
        <div className="min-h-screen bg-ivory font-sans text-ink">
          <Header />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/despre" element={<About />} />
              <Route path="/solutii" element={<Solutions />} />
              <Route path="/metoda" element={<Method />} />
              <Route path="/solutii/:slug" element={<SolutionDetail />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogArticle />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/politica-de-confidentialitate" element={<Legal type="privacy" />} />
              <Route path="/termeni-si-conditii" element={<Legal type="terms" />} />
              {SEO_PAGES.map((p) => (
                <Route key={p.slug} path={`/${p.slug}`} element={<SeoLanding page={p} />} />
              ))}
            </Routes>
          </main>
          <Footer />
          <WhatsAppFab />
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                background: "#F6EFE2",
                border: "1px solid #DCCEB4",
                color: "#2A2116",
                borderRadius: 0,
              },
            }}
          />
        </div>
      </LenisProvider>
    </BrowserRouter>
  );
}

export default App;
